import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  SimpleGrid,
  Image,
  Badge,
  Divider,
  useToast,
  Progress,
  NumberInput,
  NumberInputField,
  Switch,
  Icon,
  VStack,
  HStack
} from '@chakra-ui/react';
import { MdCloudUpload, MdAttachFile, MdImage } from 'react-icons/md';
import { campaignService } from '../../../../services/campaignService';
import { menuService } from '../../../../services/menuService';

const STEPS = [
  'Kampanya türü',
  'İndirim',
  'Zamanlama',
  'Kapsam',
  'Bilgiler',
  'Görsel',
  'Özet'
];

const DAY_OPTIONS = [
  { value: 1, label: 'Pzt' },
  { value: 2, label: 'Sal' },
  { value: 3, label: 'Çar' },
  { value: 4, label: 'Per' },
  { value: 5, label: 'Cum' },
  { value: 6, label: 'Cmt' },
  { value: 0, label: 'Paz' }
];

const initialForm = {
  campaignType: 'coupon',
  discountType: 'percentage',
  discountValue: '',
  scheduleType: 'date_range',
  recurrence: {
    daysOfWeek: [],
    allDay: true,
    startTime: '09:00',
    endTime: '17:45'
  },
  scopeType: 'all',
  scopeCategoryIds: [],
  scopeProductIds: [],
  name: '',
  description: '',
  code: '',
  startDate: '',
  endDate: '',
  isOneTimeUse: false,
  maxUsageCount: '',
  minOrderAmount: '',
  image: null
};

function hasActiveMaxUsageLimit(value) {
  if (value === '' || value == null || value === undefined) return false;
  const n = Number(value);
  return !Number.isNaN(n) && n > 0;
}

function maxUsageInputValue(value) {
  if (value === '' || value == null || value === undefined) return '';
  const n = Number(value);
  return Number.isNaN(n) ? '' : String(value);
}

function formatDiscountSummary(form) {
  const v = form.discountValue;
  if (form.discountType === 'percentage') return `%${v}`;
  if (form.discountType === 'fixed_amount') return `${v} ₺ indirim`;
  return `${v} ₺ sabit fiyat`;
}

const MAX_IMAGE_MB = 10;

const CampaignWizard = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setForm(initialForm);
    setImagePreview(null);
    setIsDragOver(false);
    const loadMenu = async () => {
      try {
        const [cats, prods] = await Promise.all([
          menuService.getCategories(),
          menuService.getProducts()
        ]);
        setCategories(Array.isArray(cats) ? cats : cats?.data || []);
        setProducts(Array.isArray(prods) ? prods : prods?.data || []);
      } catch (e) {
        console.warn('Menü verisi yüklenemedi', e);
      }
    };
    loadMenu();
  }, [isOpen]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleImageFile = useCallback(
    (file) => {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Geçersiz dosya',
          description: 'Lütfen PNG veya JPG görsel yükleyin.',
          status: 'warning',
          duration: 3000
        });
        return;
      }
      if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
        toast({
          title: 'Dosya çok büyük',
          description: `Maksimum ${MAX_IMAGE_MB}MB yükleyebilirsiniz.`,
          status: 'warning',
          duration: 3000
        });
        return;
      }
      setImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      update({ image: file });
    },
    [toast]
  );

  const removeImage = () => {
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    update({ image: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageFile(file);
  };

  const validateStep = () => {
    if (step === 1) {
      const v = Number(form.discountValue);
      if (!v && v !== 0) return 'İndirim değeri girin';
      if (form.discountType === 'percentage' && (v < 0 || v > 100)) {
        return 'Yüzde 0-100 arasında olmalı';
      }
      if (form.discountType !== 'percentage' && v <= 0) {
        return 'Geçerli bir tutar girin';
      }
    }
    if (step === 2 && form.scheduleType === 'recurring') {
      if (!form.recurrence.daysOfWeek?.length) return 'En az bir gün seçin';
    }
    if (step === 3) {
      if (form.scopeType === 'categories' && !form.scopeCategoryIds.length) {
        return 'En az bir kategori seçin';
      }
      if (form.scopeType === 'products' && !form.scopeProductIds.length) {
        return 'En az bir ürün seçin';
      }
    }
    if (step === 4) {
      if (!form.name.trim()) return 'Kampanya adı gerekli';
      if (!form.description.trim()) return 'Açıklama gerekli';
      if (!form.startDate || !form.endDate) return 'Başlangıç ve bitiş tarihi gerekli';
      if (form.campaignType === 'coupon' && !form.code.trim()) return 'Kupon kodu gerekli';
    }
    if (step === 5 && !form.image) return 'Kampanya görseli yükleyin';
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) {
      toast({ title: err, status: 'warning', duration: 3000 });
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) {
      toast({ title: err, status: 'warning', duration: 3000 });
      return;
    }
    setIsLoading(true);
    try {
      await campaignService.createCampaign({
        name: form.name.trim(),
        description: form.description.trim(),
        campaignType: form.campaignType,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        discount: form.discountType === 'percentage' ? Number(form.discountValue) : 0,
        scheduleType: form.scheduleType,
        recurrence: form.recurrence,
        scopeType: form.scopeType,
        scopeCategoryIds: form.scopeCategoryIds,
        scopeProductIds: form.scopeProductIds,
        applyToMenu: form.campaignType === 'automatic',
        code: form.campaignType === 'coupon' ? form.code.trim().toUpperCase() : undefined,
        startDate: form.startDate,
        endDate: form.endDate,
        image: form.image,
        isOneTimeUse: form.isOneTimeUse,
        maxUsageCount: hasActiveMaxUsageLimit(form.maxUsageCount)
          ? Number(form.maxUsageCount)
          : '',
        minOrderAmount:
          form.minOrderAmount !== '' && form.minOrderAmount != null
            ? Number(form.minOrderAmount)
            : ''
      });
      toast({
        title: 'Başarılı',
        description: 'Kampanya oluşturuldu.',
        status: 'success',
        duration: 3000
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {[
              {
                key: 'coupon',
                title: 'Kuponlu kampanya',
                desc: 'Müşteri ödeme sırasında kod girer.'
              },
              {
                key: 'automatic',
                title: 'Otomatik (kodsuz)',
                desc: 'Menüde fiyat otomatik düşer, kod gerekmez.'
              }
            ].map((opt) => (
              <Box
                key={opt.key}
                p={5}
                border="2px solid"
                borderColor={form.campaignType === opt.key ? 'green.500' : 'gray.200'}
                borderRadius="lg"
                cursor="pointer"
                bg={form.campaignType === opt.key ? 'green.50' : 'white'}
                onClick={() => update({ campaignType: opt.key })}
              >
                <Text fontWeight="bold">{opt.title}</Text>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {opt.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        );

      case 1:
        return (
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>İndirim tipi</FormLabel>
              <Select
                value={form.discountType}
                onChange={(e) => update({ discountType: e.target.value })}
              >
                <option value="percentage">Yüzde (%)</option>
                <option value="fixed_amount">Sabit tutar (₺ indirim)</option>
                <option value="fixed_price">Sabit fiyat (₺)</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>
                {form.discountType === 'percentage'
                  ? 'İndirim oranı (%)'
                  : form.discountType === 'fixed_amount'
                    ? 'İndirim tutarı (₺)'
                    : 'Yeni sabit fiyat (₺)'}
              </FormLabel>
              <NumberInput
                value={form.discountValue}
                onChange={(_, v) => update({ discountValue: v })}
                min={0}
                max={form.discountType === 'percentage' ? 100 : undefined}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Zamanlama</FormLabel>
              <Select
                value={form.scheduleType}
                onChange={(e) => update({ scheduleType: e.target.value })}
              >
                <option value="date_range">Tarih aralığı (sürekli geçerli)</option>
                <option value="recurring">Tekrarlayan (belirli gün/saat)</option>
              </Select>
            </FormControl>
            {form.scheduleType === 'recurring' && (
              <>
                <FormControl>
                  <FormLabel>Günler</FormLabel>
                  <CheckboxGroup
                    value={form.recurrence.daysOfWeek.map(String)}
                    onChange={(vals) =>
                      update({
                        recurrence: {
                          ...form.recurrence,
                          daysOfWeek: vals.map(Number)
                        }
                      })
                    }
                  >
                    <Flex wrap="wrap" gap={2}>
                      {DAY_OPTIONS.map((d) => (
                        <Checkbox key={d.value} value={String(d.value)}>
                          {d.label}
                        </Checkbox>
                      ))}
                    </Flex>
                  </CheckboxGroup>
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0}>Tüm gün</FormLabel>
                  <Switch
                    isChecked={form.recurrence.allDay}
                    onChange={(e) =>
                      update({
                        recurrence: { ...form.recurrence, allDay: e.target.checked }
                      })
                    }
                  />
                </FormControl>
                {!form.recurrence.allDay && (
                  <Flex gap={4}>
                    <FormControl>
                      <FormLabel>Başlangıç saati</FormLabel>
                      <Input
                        type="time"
                        value={form.recurrence.startTime}
                        onChange={(e) =>
                          update({
                            recurrence: { ...form.recurrence, startTime: e.target.value }
                          })
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Bitiş saati</FormLabel>
                      <Input
                        type="time"
                        value={form.recurrence.endTime}
                        onChange={(e) =>
                          update({
                            recurrence: { ...form.recurrence, endTime: e.target.value }
                          })
                        }
                      />
                    </FormControl>
                  </Flex>
                )}
                <Text fontSize="sm" color="gray.500">
                  Kampanya yine de genel başlangıç/bitiş tarihleri içinde geçerlidir (sonraki adım).
                </Text>
              </>
            )}
          </Stack>
        );

      case 3:
        return (
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Kapsam</FormLabel>
              <Select
                value={form.scopeType}
                onChange={(e) =>
                  update({
                    scopeType: e.target.value,
                    scopeCategoryIds: [],
                    scopeProductIds: []
                  })
                }
              >
                <option value="all">Tüm menü</option>
                <option value="categories">Seçili kategoriler</option>
                <option value="products">Seçili ürünler</option>
              </Select>
            </FormControl>
            {form.scopeType === 'categories' && (
              <CheckboxGroup
                value={form.scopeCategoryIds}
                onChange={(vals) => update({ scopeCategoryIds: vals })}
              >
                <Stack maxH="240px" overflowY="auto">
                  {categories.map((c) => (
                    <Checkbox key={c._id} value={c._id}>
                      {c.name}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            )}
            {form.scopeType === 'products' && (
              <CheckboxGroup
                value={form.scopeProductIds}
                onChange={(vals) => update({ scopeProductIds: vals })}
              >
                <Stack maxH="240px" overflowY="auto">
                  {products.map((p) => (
                    <Checkbox key={p._id} value={p._id}>
                      {p.name} — {p.price} ₺
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            )}
          </Stack>
        );

      case 4:
        return (
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Kampanya adı</FormLabel>
              <Input value={form.name} onChange={(e) => update({ name: e.target.value })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Açıklama</FormLabel>
              <Textarea
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </FormControl>
            <Flex gap={4}>
              <FormControl isRequired>
                <FormLabel>Başlangıç</FormLabel>
                <Input
                  type="datetime-local"
                  value={form.startDate}
                  onChange={(e) => update({ startDate: e.target.value })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Bitiş</FormLabel>
                <Input
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => update({ endDate: e.target.value })}
                />
              </FormControl>
            </Flex>
            {form.campaignType === 'coupon' && (
              <FormControl isRequired>
                <FormLabel>Kupon kodu</FormLabel>
                <Input
                  value={form.code}
                  onChange={(e) => update({ code: e.target.value.toUpperCase() })}
                  placeholder="ORNEK20"
                />
              </FormControl>
            )}
            <FormControl>
              <FormLabel>Minimum sepet tutarı (₺)</FormLabel>
              <Input
                type="number"
                min={0}
                placeholder="Yok (sınırsız alt limit)"
                value={form.minOrderAmount}
                onChange={(e) => update({ minOrderAmount: e.target.value })}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Boş = minimum yok. Örn. 200 yazarsanız sepet ara toplamı en az 200 ₺ olmalı.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel>Maks. toplam kullanım</FormLabel>
              <Input
                type="number"
                min={1}
                placeholder="Sınırsız"
                value={maxUsageInputValue(form.maxUsageCount)}
                onChange={(e) => {
                  const v = e.target.value;
                  update({ maxUsageCount: v === '' ? '' : v });
                }}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Boş bırakın = sınırsız toplam kullanım (tüm müşteriler).
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel mb={1}>Tek kullanımlık (kullanıcı başına)</FormLabel>
              <HStack align="start">
                <Switch
                  isChecked={form.isOneTimeUse}
                  onChange={(e) => update({ isOneTimeUse: e.target.checked })}
                />
                <Text fontSize="xs" color="gray.600" lineHeight="tall">
                  {form.isOneTimeUse
                    ? 'Her müşteri yalnızca bir kez kullanır.'
                    : 'Aynı müşteri kampanya süresince tekrar kullanabilir.'}
                </Text>
              </HStack>
            </FormControl>
          </Stack>
        );

      case 5:
        return (
          <FormControl isRequired>
            <FormLabel fontWeight="bold" mb={3}>
              Kampanya görseli
            </FormLabel>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              display="none"
              onChange={(e) => handleImageFile(e.target.files?.[0])}
            />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <VStack align="stretch" spacing={3}>
                {form.image ? (
                  <Box
                    p={4}
                    borderRadius="12px"
                    bg="gray.50"
                    border="2px dashed"
                    borderColor="#e45858"
                  >
                    <Flex align="center" justify="space-between" mb={3}>
                      <Flex align="center" gap={3} minW={0}>
                        <Icon as={MdAttachFile} color="#e45858" w={6} h={6} flexShrink={0} />
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                            {form.image.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {(form.image.size / 1024 / 1024).toFixed(2)} MB
                          </Text>
                        </Box>
                      </Flex>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={removeImage}
                        _hover={{ bg: 'red.50' }}
                        flexShrink={0}
                      >
                        Kaldır
                      </Button>
                    </Flex>
                    <Button
                      size="sm"
                      w="full"
                      variant="outline"
                      colorScheme="red"
                      leftIcon={<Icon as={MdCloudUpload} />}
                      onClick={openFilePicker}
                    >
                      Başka görsel seç
                    </Button>
                  </Box>
                ) : (
                  <Box
                    as="button"
                    type="button"
                    onClick={openFilePicker}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    w="100%"
                    minH="200px"
                    p={8}
                    borderRadius="12px"
                    bg={isDragOver ? 'red.50' : 'gray.50'}
                    border="2px dashed"
                    borderColor={isDragOver ? '#e45858' : 'gray.300'}
                    _hover={{
                      borderColor: '#e45858',
                      bg: 'gray.100'
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                  >
                    <Flex direction="column" align="center" gap={2}>
                      <Icon as={MdCloudUpload} color="#e45858" w={10} h={10} />
                      <Text fontSize="md" fontWeight="600" color="gray.800">
                        Görsel seç veya sürükle-bırak
                      </Text>
                      <Text fontSize="xs" color="gray.600" textAlign="center" lineHeight="tall">
                        PNG, JPG, WEBP (maks. {MAX_IMAGE_MB}MB)
                        <br />
                        Önerilen: 1080×1920px (dikey, 9:16)
                      </Text>
                    </Flex>
                  </Box>
                )}
                <Box
                  p={3}
                  borderRadius="10px"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <HStack align="start" spacing={2}>
                    <Icon as={MdImage} color="blue.500" mt={0.5} />
                    <Text fontSize="xs" color="blue.800" lineHeight="tall">
                      Bu görsel mobil uygulamada kampanya duyurusunda gösterilir. Net,
                      okunaklı ve markanıza uygun bir görsel kullanın.
                    </Text>
                  </HStack>
                </Box>
              </VStack>

              <Box
                borderRadius="16px"
                overflow="hidden"
                bg="gray.100"
                border="1px solid"
                borderColor="gray.200"
                minH={{ base: '280px', md: '360px' }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
              >
                {imagePreview ? (
                  <>
                    <Image
                      src={imagePreview}
                      alt="Kampanya önizleme"
                      w="100%"
                      h="100%"
                      minH={{ base: '280px', md: '360px' }}
                      objectFit="cover"
                    />
                    <Badge
                      position="absolute"
                      top={3}
                      left={3}
                      colorScheme="green"
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      Önizleme
                    </Badge>
                  </>
                ) : (
                  <VStack spacing={2} color="gray.500" p={6} textAlign="center">
                    <Icon as={MdImage} w={12} h={12} opacity={0.4} />
                    <Text fontSize="sm" fontWeight="500">
                      Önizleme burada görünür
                    </Text>
                    <Text fontSize="xs" maxW="200px">
                      Dikey kampanya görseli mobilde en iyi sonucu verir
                    </Text>
                  </VStack>
                )}
              </Box>
            </SimpleGrid>
          </FormControl>
        );

      case 6:
        return (
          <Stack spacing={3} fontSize="sm">
            <Flex justify="space-between">
              <Text color="gray.600">Tür</Text>
              <Badge colorScheme={form.campaignType === 'automatic' ? 'purple' : 'green'}>
                {form.campaignType === 'automatic' ? 'Otomatik' : 'Kuponlu'}
              </Badge>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">İndirim</Text>
              <Text fontWeight="semibold">{formatDiscountSummary(form)}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">Zamanlama</Text>
              <Text>
                {form.scheduleType === 'recurring'
                  ? `Tekrarlayan (${form.recurrence.daysOfWeek.length} gün)`
                  : 'Tarih aralığı'}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text color="gray.600">Kapsam</Text>
              <Text>
                {form.scopeType === 'all'
                  ? 'Tüm menü'
                  : form.scopeType === 'categories'
                    ? `${form.scopeCategoryIds.length} kategori`
                    : `${form.scopeProductIds.length} ürün`}
              </Text>
            </Flex>
            {form.campaignType === 'coupon' && (
              <Flex justify="space-between">
                <Text color="gray.600">Kod</Text>
                <Text fontFamily="mono">{form.code}</Text>
              </Flex>
            )}
            {form.minOrderAmount !== '' && form.minOrderAmount != null && (
              <Flex justify="space-between">
                <Text color="gray.600">Min. sepet</Text>
                <Text fontWeight="semibold">{form.minOrderAmount} ₺</Text>
              </Flex>
            )}
            <Divider />
            {imagePreview && (
              <Box borderRadius="12px" overflow="hidden" maxH="160px">
                <Image src={imagePreview} alt="" w="100%" maxH="160px" objectFit="cover" />
              </Box>
            )}
            <Text fontWeight="bold">{form.name}</Text>
            <Text color="gray.600">{form.description}</Text>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay
        bg="blackAlpha.500"
        backdropFilter="blur(10px)"
        sx={{ WebkitBackdropFilter: 'blur(10px)' }}
      />
      <ModalContent>
        <ModalHeader>Kampanya Sihirbazı</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Progress
            value={((step + 1) / STEPS.length) * 100}
            size="xs"
            colorScheme="green"
            mb={4}
            borderRadius="full"
          />
          <Text fontSize="sm" color="gray.500" mb={4}>
            Adım {step + 1}/{STEPS.length}: {STEPS[step]}
          </Text>
          {renderStep()}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
            İptal
          </Button>
          {step > 0 && (
            <Button variant="outline" mr={3} onClick={handleBack} isDisabled={isLoading}>
              Geri
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button colorScheme="green" onClick={handleNext}>
              İleri
            </Button>
          ) : (
            <Button colorScheme="green" onClick={handleSubmit} isLoading={isLoading}>
              Oluştur
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CampaignWizard;
